import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ChartConfiguration } from "chart.js";
import { useForm, Controller } from "react-hook-form";
import api from "../../../utils/api";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import InfiniteDeviceSelect from "../../../components/InfiniteDeviceSelect";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { ReportChart, gridColor, tickColor, reportLegend } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import ReportActionButton from "../components/ReportActionButton";
import { resolveTz } from "../utils/reportDateFormat";
import {
  type DrivingHistoryResponse,
  type HistoryBlock,
  type HistoryHour,
  type Tile,
  behaviorLabel,
  fmtDay,
  fmtMinutes,
  showScore,
  telemetryTiles,
  todayIn,
  totalTiles,
} from "./drivingHistoryView";

const MAX_DAYS = 31;
const DRIVING = "#16A34A";
const SCORE_COLORS = { green: "#16A34A", yellow: "#D97706", red: "#DC2626", gray: "#9CA3AF" } as const;

interface FormData {
  devices: string[];
  from: string;
  to: string;
}

interface Query {
  deviceId: string;
  from: string;
  to: string;
  tz: string;
}

const fetchHistory = async (q: Query, signal?: AbortSignal): Promise<DrivingHistoryResponse> => {
  const res = await api.get(`/drivingHistory/${encodeURIComponent(q.deviceId)}`, {
    params: { from: q.from, to: q.to, tz: q.tz },
    signal,
  });
  return (res.data?.data ?? res.data) as DrivingHistoryResponse;
};

// Número grande + unidad chica, como el panel «Hoy» de Mapon (1h 54min).
function BigValue({ value, className = "" }: { value: string; className?: string }) {
  const parts = value.split(/(\d+(?:\.\d+)?)/).filter(Boolean);
  return (
    <span className={`tabular-nums ${className}`}>
      {parts.map((p, i) =>
        /^\d/.test(p) ? (
          <span key={i} className="text-2xl font-semibold tracking-tight">{p}</span>
        ) : (
          <span key={i} className="text-xs font-medium opacity-60 ml-0.5 mr-1">{p.trim()}</span>
        ),
      )}
    </span>
  );
}

function TileGrid({ tiles, isDarkMode }: { tiles: Tile[]; isDarkMode: boolean }) {
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px rounded-xl overflow-hidden border ${isDarkMode ? "border-white/10 bg-white/10" : "border-black/5 bg-black/5"}`}>
      {tiles.map((t) => (
        <div key={t.key} className={`px-3 py-3 ${isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900"}`}>
          <BigValue value={t.value} />
          <p className={`mt-1 flex items-center gap-1 text-[11px] uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            <i className={`${t.icon} text-sm`} />
            {t.label}
          </p>
        </div>
      ))}
    </div>
  );
}

const blocksChart = (blocks: HistoryBlock[], isDarkMode: boolean): ChartConfiguration => ({
  type: "bar",
  data: {
    labels: blocks.map((b) => `${String(b.from_hour).padStart(2, "0")}–${String(b.from_hour + 3).padStart(2, "0")}`),
    datasets: [
      { label: "Al volante", data: blocks.map((b) => b.driving_min), backgroundColor: DRIVING, borderRadius: 4, maxBarThickness: 34 },
      {
        label: "Detenido",
        data: blocks.map((b) => b.stopped_min),
        backgroundColor: isDarkMode ? "rgba(255,255,255,0.22)" : "#D1D5DB",
        borderRadius: 4,
        maxBarThickness: 34,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: reportLegend(isDarkMode),
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtMinutes(Number(c.raw))}` } },
    },
    scales: {
      x: { stacked: true, ticks: { color: tickColor(isDarkMode), font: { size: 10 } }, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, ticks: { color: tickColor(isDarkMode), precision: 0 }, grid: { color: gridColor(isDarkMode) }, title: { display: true, text: "min", color: tickColor(isDarkMode) } },
    },
  },
});

// Sin eventos en el rango no se dibuja la serie ni su eje (§3.1: lo que no
// existe no se pinta).
const hourlyChart = (hours: HistoryHour[], isDarkMode: boolean): ChartConfiguration => {
  const hasEvents = hours.some((h) => h.events > 0);
  return {
  type: "bar",
  data: {
    labels: hours.map((h) => String(h.hour).padStart(2, "0")),
    datasets: [
      {
        type: "bar",
        label: "Velocidad media (km/h)",
        data: hours.map((h) => h.avg_speed_kmh),
        backgroundColor: "rgba(37,99,235,0.75)",
        borderRadius: 3,
        maxBarThickness: 18,
        yAxisID: "y",
      },
      ...(hasEvents ? [{
        type: "line",
        label: "Eventos",
        data: hours.map((h) => h.events),
        borderColor: "#DC2626",
        backgroundColor: "#DC2626",
        pointRadius: hours.map((h) => (h.events > 0 ? 4 : 0)),
        borderWidth: 0,
        showLine: false,
        yAxisID: "y1",
      }] : []),
    ],
  } as ChartConfiguration["data"],
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: reportLegend(isDarkMode) },
    scales: {
      x: { ticks: { color: tickColor(isDarkMode), font: { size: 10 }, maxRotation: 0, autoSkip: true }, grid: { display: false } },
      y: { beginAtZero: true, ticks: { color: tickColor(isDarkMode) }, grid: { color: gridColor(isDarkMode) } },
      y1: { display: hasEvents, beginAtZero: true, position: "right", ticks: { color: "#DC2626", precision: 0 }, grid: { display: false } },
    },
  },
  };
};

export default function DrivingHistorySection() {
  const { isDarkMode } = useDarkMode();
  const theme = getThemeClasses(isDarkMode);
  const tz = resolveTz(null);
  const today = todayIn(tz);
  const [query, setQuery] = useState<Query | null>(null);

  const { control, register, handleSubmit, setValue, watch } = useForm<FormData>({
    defaultValues: { devices: [], from: today, to: today },
  });
  const { disableWithoutCompany } = useReportCompanyScope(setValue, () => setQuery(null));
  const [devices, from, to] = watch(["devices", "from", "to"]);

  const span = from && to ? (Date.parse(to) - Date.parse(from)) / 86400000 + 1 : 0;
  const rangeError = span <= 0 ? "La fecha final es anterior a la inicial" : span > MAX_DAYS ? `Máximo ${MAX_DAYS} días` : null;
  const canSubmit = devices.length === 1 && !rangeError && !disableWithoutCompany;

  // La clave incluye unidad y fechas: si el usuario cambia antes de que
  // termine, la respuesta anterior no se pinta.
  const { data, isFetching, error } = useQuery({
    queryKey: ["drivingHistory", query],
    queryFn: ({ signal }) => fetchHistory(query as Query, signal),
    enabled: query !== null,
    staleTime: 60_000,
  });

  const onSubmit = (f: FormData) => {
    if (!canSubmit) return;
    setQuery({ deviceId: f.devices[0], from: f.from, to: f.to, tz });
  };

  const isPeriod = data ? data.from !== data.to : false;
  const day = data?.days[0];
  const view: NonNullable<DrivingHistoryResponse["period"]> | null = !data
    ? null
    : isPeriod
      ? data.period
      : day?.totals
        ? { ...day, totals: day.totals }
        : null;
  const score = view?.score;
  const telemetry = view ? telemetryTiles(view.telemetry) : [];
  const blocks = useMemo(() => (view ? blocksChart(view.blocks, isDarkMode) : null), [view, isDarkMode]);
  const hourly = useMemo(() => (view ? hourlyChart(view.hourly, isDarkMode) : null), [view, isDarkMode]);

  const card = `rounded-2xl border ${theme.cardBorder} ${theme.cardBg}`;
  const inputCls = `h-10 w-full rounded-lg border px-3 text-sm ${theme.inputBg} ${theme.inputBorder} ${theme.inputText}`;

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`${card} p-3 sm:p-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_9.5rem_9.5rem_auto] lg:items-end`}>
        <label className="flex flex-col gap-1 min-w-0">
          <span className={`text-xs ${theme.textSecondary}`}>Unidad</span>
          <Controller
            name="devices"
            control={control}
            render={({ field }) => (
              <InfiniteDeviceSelect
                value={field.value}
                // Una sola unidad: la última que se eligió reemplaza a la anterior.
                onChange={(ids) => field.onChange(ids.slice(-1))}
                isDarkMode={isDarkMode}
                themeClasses={theme}
              />
            )}
          />
        </label>
        <div className="grid grid-cols-2 gap-3 lg:contents">
          <label className="flex flex-col gap-1 min-w-0">
            <span className={`text-xs ${theme.textSecondary}`}>Desde</span>
            <input type="date" max={today} {...register("from")} className={inputCls} />
          </label>
          <label className="flex flex-col gap-1 min-w-0">
            <span className={`text-xs ${theme.textSecondary}`}>Hasta</span>
            <input type="date" max={today} {...register("to")} className={inputCls} />
          </label>
        </div>
        <ReportActionButton type="submit" disabled={!canSubmit || isFetching}>
          {isFetching ? "Midiendo…" : "Ver historial"}
        </ReportActionButton>
        {rangeError && <p className="text-xs text-red-500 lg:col-span-4">{rangeError}</p>}
      </form>

      {isFetching && (
        <ReportLoadingPanel isDarkMode={isDarkMode} icon="ri-steering-2-line" title="Midiendo la jornada…" subtitle="Leyendo posiciones y eventos" />
      )}

      {!isFetching && error && (
        <div className={`${card} mt-4 p-4 text-sm text-red-500`}>No se pudo obtener el historial de conducción.</div>
      )}

      {!isFetching && data && (
        <div className="mt-4 flex flex-col gap-4 animate-fadeIn">
          <header className={`${card} p-4 flex flex-wrap items-end justify-between gap-3`}>
            <div className="min-w-0">
              <p className={`text-[11px] uppercase tracking-[0.14em] ${theme.textSecondary}`}>
                Historial de conducción · {isPeriod ? `${fmtDay(data.from)} – ${fmtDay(data.to)}` : fmtDay(data.from)}
              </p>
              <h2 className={`mt-1 text-xl font-semibold truncate ${theme.text}`}>
                {data.unit.name ?? data.unit.device_id}
                {data.unit.plate && <span className={`ml-2 text-sm font-normal ${theme.textSecondary}`}>{data.unit.plate}</span>}
              </h2>
              <p className={`mt-0.5 text-sm ${theme.textSecondary}`}>
                <i className="ri-user-3-line mr-1" />
                {data.unit.driver?.name ?? "La unidad no tiene conductor asignado"}
              </p>
            </div>
            {showScore(score) && score && (
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white"
                  style={{ background: SCORE_COLORS[score.color] }}
                >
                  {score.index}
                </span>
                <span className={`text-xs ${theme.textSecondary}`}>Índice del {isPeriod ? "periodo" : "día"}</span>
              </div>
            )}
          </header>

          {!view ? (
            <div className={`${card} p-6 text-sm ${theme.textSecondary}`}>
              La unidad no reportó suficientes posiciones para medir la jornada.
            </div>
          ) : (
            <>
              <TileGrid tiles={totalTiles(view.totals)} isDarkMode={isDarkMode} />

              {telemetry.length > 0 && (
                <section className={`${card} p-4`}>
                  <h3 className={`text-sm font-semibold mb-3 ${theme.text}`}>Telemetría del motor</h3>
                  <TileGrid tiles={telemetry} isDarkMode={isDarkMode} />
                </section>
              )}

              {showScore(score) && score && score.breakdown.length > 0 && (
                <section className={`${card} p-4`}>
                  <h3 className={`text-sm font-semibold mb-3 ${theme.text}`}>Desglose de alertas</h3>
                  <ul className="flex flex-col gap-2">
                    {score.breakdown.map((b) => (
                      <li key={b.type} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 text-sm">
                        <span className={theme.text}>{behaviorLabel(b.type)}</span>
                        <span className={`tabular-nums ${theme.textSecondary}`}>{b.count} · {b.pct}%</span>
                        <span className={`col-span-2 h-1.5 rounded-full overflow-hidden ${isDarkMode ? "bg-white/10" : "bg-black/5"}`}>
                          <span className="block h-full rounded-full" style={{ width: `${b.pct}%`, background: SCORE_COLORS[score.color] }} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
                <section className={`${card} p-4 min-w-0`}>
                  <h3 className={`text-sm font-semibold ${theme.text}`}>Volante contra detenido</h3>
                  <p className={`text-xs mb-2 ${theme.textSecondary}`}>Minutos por bloque de 3 horas</p>
                  {blocks && <ReportChart config={blocks} height={240} />}
                </section>
                <section className={`${card} p-4 min-w-0`}>
                  <h3 className={`text-sm font-semibold ${theme.text}`}>{view.totals.events > 0 ? "Velocidad media y eventos" : "Velocidad media"}</h3>
                  <p className={`text-xs mb-2 ${theme.textSecondary}`}>Hora por hora</p>
                  {hourly && <ReportChart config={hourly} height={240} />}
                </section>
              </div>

              {isPeriod && (
                <section className={`${card} overflow-hidden`}>
                  <h3 className={`px-4 pt-4 pb-2 text-sm font-semibold ${theme.text}`}>Día por día</h3>
                  <ul className={`divide-y ${isDarkMode ? "divide-white/10" : "divide-gray-100"}`}>
                    {data.days.map((d) => (
                      <li key={d.date} className="px-4 py-2.5 grid grid-cols-[5.5rem_minmax(0,1fr)] gap-2 text-sm">
                        <span className={`tabular-nums ${theme.textSecondary}`}>{fmtDay(d.date).slice(0, 5)}</span>
                        {d.totals ? (
                          <span className={`truncate tabular-nums ${theme.text}`}>
                            {fmtMinutes(d.totals.driving_min)} al volante · {Math.round(d.totals.km)} km
                            {d.totals.events > 0 && ` · ${d.totals.events} eventos`}
                          </span>
                        ) : (
                          <span className={theme.textSecondary}>Sin jornada</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {!data && !isFetching && !error && (
        <div className={`${card} mt-4 p-8 flex flex-col items-center text-center gap-2`}>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-steering-2-line text-2xl text-primary-500" />
          </span>
          <p className={`text-sm font-semibold ${theme.text}`}>Historial de conducción</p>
          <p className={`text-xs max-w-sm ${theme.textSecondary}`}>
            Elige una unidad y un día, o un periodo de hasta {MAX_DAYS} días, para ver su jornada: tiempo al volante, kilómetros, ralentí, descanso y su actividad hora por hora.
          </p>
        </div>
      )}
    </div>
  );
}
